import { useEffect, useState } from "react";
import { X, CheckCircle2, XCircle, Wrench, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function TeamDiagnoseModal({ member, t, onClose }) {
  const [diag, setDiag] = useState(null);
  const [busy, setBusy] = useState(true);
  const [repairing, setRepairing] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/team/${member.id}/diagnose`);
      setDiag(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    }
    setBusy(false);
  };
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const repair = async () => {
    setRepairing(true);
    try {
      const r = await api.post(`/team/${member.id}/repair`);
      setDiag(r.data);
      toast.success(t("diag_repaired"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    }
    setRepairing(false);
  };

  const hasFixable = !!diag && diag.checks.some((c) => !c.ok && c.fixable);
  const hasUnfixable = !!diag && diag.checks.some((c) => !c.ok && !c.fixable);

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="team-diagnose-modal"
        className="bg-background border border-border w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border bg-surface flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Stethoscope size={16} className="text-primary" />
            <div>
              <p className="label-eyebrow text-primary">{t("diag_title")}</p>
              <p className="font-display font-bold uppercase text-sm">{member.full_name}</p>
            </div>
          </div>
          <button data-testid="diag-close" onClick={onClose} className="p-1.5 border border-border hover:border-primary hover:text-primary transition-colors">
            <X size={15} />
          </button>
        </div>

        <div className="p-5">
          {busy ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-10 bg-surface animate-pulse" />
              ))}
            </div>
          ) : !diag ? (
            <p className="text-text-secondary text-sm text-center py-8">{t("error_generic")}</p>
          ) : (
            <>
              {diag.healthy && (
                <div data-testid="diag-healthy" className="border border-success/40 bg-success/10 text-success text-sm px-4 py-3 mb-4 flex items-center gap-2">
                  <CheckCircle2 size={16} /> {t("diag_healthy")}
                </div>
              )}
              {diag.fixes_applied?.length > 0 && (
                <div className="border border-success/40 bg-success/10 text-success text-xs px-4 py-3 mb-4">
                  {t("diag_repaired")} — {diag.fixes_applied.join(" · ")}
                </div>
              )}
              <div className="border border-border divide-y divide-border">
                {diag.checks.map((c) => (
                  <div key={c.key} data-testid={`diag-check-${c.key}`} className="flex items-start gap-3 px-4 py-3">
                    {c.ok ? (
                      <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" />
                    ) : (
                      <XCircle size={16} className="text-primary shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm font-display font-bold uppercase tracking-wide ${c.ok ? "" : "text-primary"}`}>
                        {t(`diag_check_${c.key}`)}
                      </p>
                      {c.detail && <p className="text-xs text-text-secondary mt-0.5">{c.detail}</p>}
                    </div>
                  </div>
                ))}
              </div>
              {hasUnfixable && !diag.healthy && (
                <p className="text-xs text-text-secondary mt-3">{t("diag_fix_note")}</p>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 border border-border hover:border-white text-xs font-display font-bold uppercase tracking-widest transition-colors">
            {t("close")}
          </button>
          {hasFixable && (
            <button
              data-testid="repair-member-btn"
              onClick={repair}
              disabled={repairing}
              className="px-4 h-10 bg-primary hover:bg-primary-hover text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60 transition-colors"
            >
              <Wrench size={13} /> {repairing ? "…" : t("diag_repair")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
